// ============================================================================
// OPS VIEW (BLOCK 14 - Platform Admin Only)
// Enhanced Failure Inbox + Operator Controls + Test Error Injection
//
// Features:
// - Detailed error cards with expandable debug info
// - Resolve Modal with resolution type and notes
// - Copy debug bundle functionality
// - Undo resolution (10s window)
// - Enhanced test error injection with category-specific fields
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertCircle, CheckCircle, AlertTriangle, XCircle, RefreshCcw, Filter,
  ExternalLink, FlaskConical, ChevronDown, ChevronUp, Loader2,
  Copy, X, Undo2, Code, MapPin, User, Link2, Clock, Shield, Zap
} from 'lucide-react';
import {
  getOpsErrors, markOpsErrorResolved, undoOpsErrorResolution,
  getTenants, injectOpsTestError, isPlatformAdmin
} from '../core/index.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { safeUserError } from '../core/safeError.js';

// ============================================================================
// CONSTANTS
// ============================================================================

const RESOLUTION_TYPES = [
  { value: 'fixed_by_deploy', label: 'Fixed by deploy', description: 'Code fix was deployed' },
  { value: 'retried_successfully', label: 'Retried successfully', description: 'User retried and it worked' },
  { value: 'role_corrected', label: 'Role corrected', description: 'User permissions were fixed' },
  { value: 'acknowledged_expected', label: 'Acknowledged (expected)', description: 'Known issue, expected behavior' },
  { value: 'data_repaired', label: 'Data repaired', description: 'Database/data was fixed manually' },
];

const SEVERITY_CONFIG = {
  critical: { color: 'red', label: 'Critical', icon: XCircle },
  error: { color: 'red', label: 'Error', icon: AlertCircle },
  warn: { color: 'amber', label: 'Warning', icon: AlertTriangle },
  info: { color: 'blue', label: 'Info', icon: AlertCircle },
};

// ============================================================================
// RESOLVE MODAL COMPONENT
// ============================================================================

const ResolveModal = ({ isOpen, onClose, onConfirm, errorDoc, isResolving }) => {
  const [resolutionType, setResolutionType] = useState('');
  const [resolutionNote, setResolutionNote] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setResolutionType('');
      setResolutionNote('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!resolutionType) return;
    onConfirm({ resolutionType, resolutionNote });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Resolve Issue</h3>
              <p className="text-xs text-slate-400">ID: {errorDoc?.errorId?.substring(0, 12)}...</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Resolution Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Resolution Type <span className="text-red-400">*</span>
          </label>
          <select
            value={resolutionType}
            onChange={(e) => setResolutionType(e.target.value)}
            className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50"
          >
            <option value="">Select how this was resolved...</option>
            {RESOLUTION_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {resolutionType && (
            <p className="mt-1 text-xs text-slate-500">
              {RESOLUTION_TYPES.find(t => t.value === resolutionType)?.description}
            </p>
          )}
        </div>

        {/* Resolution Note */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Resolution Note <span className="text-slate-500">(optional)</span>
          </label>
          <textarea
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
            placeholder="Add any additional context about the resolution..."
            rows={3}
            maxLength={1000}
            className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50 resize-none"
          />
          <p className="mt-1 text-xs text-slate-500 text-right">
            {resolutionNote.length}/1000
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isResolving}
            className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!resolutionType || isResolving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            {isResolving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Resolving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Resolve
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ERROR CARD COMPONENT
// ============================================================================

const ErrorCard = ({
  errorDoc,
  tenantName,
  onResolve,
  onUndo,
  isResolving,
  canUndo,
  userIsPlatformAdmin,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const severity = errorDoc.severity || 'error';
  const severityConfig = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.error;
  const SeverityIcon = severityConfig.icon;

  const getCategoryColor = (category) => {
    switch (category) {
      case 'submit_failed': return 'red';
      case 'permission_denied': return 'amber';
      case 'rollup_failed': return 'yellow';
      case 'runtime_error': return 'purple';
      default: return 'slate';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const copyDebugBundle = () => {
    const bundle = {
      errorId: errorDoc.errorId,
      tenantId: errorDoc.tenantId,
      category: errorDoc.category,
      severity: errorDoc.severity,
      message: errorDoc.message,
      errorCode: errorDoc.errorCode,
      location: errorDoc.location,
      route: errorDoc.route,
      path: errorDoc.path || errorDoc.lastFirestorePath,
      operation: errorDoc.lastOperation,
      actor: errorDoc.actor,
      entity: errorDoc.entity,
      context: errorDoc.context,
      details: errorDoc.details,
      stack: errorDoc.stack || errorDoc.stackPreview,
      where: errorDoc.where,
      createdAt: errorDoc.createdAt?.toDate?.()?.toISOString() || errorDoc.createdAt,
    };

    navigator.clipboard.writeText(JSON.stringify(bundle, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const color = getCategoryColor(errorDoc.category);
  const resolutionTypeLabel = RESOLUTION_TYPES.find(t => t.value === errorDoc.resolutionType)?.label;

  return (
    <div className={`p-4 ${errorDoc.resolved ? 'opacity-70' : ''}`}>
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-lg bg-${severityConfig.color}-500/20 flex items-center justify-center flex-shrink-0`}>
          <SeverityIcon className={`w-5 h-5 text-${severityConfig.color}-400`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header Row */}
          <div className="flex items-start justify-between gap-4 mb-2">
            <div className="flex-1 min-w-0">
              {/* Badges Row */}
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${color}-500/20 text-${color}-400`}>
                  {errorDoc.category?.replace(/_/g, ' ')}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${severityConfig.color}-500/10 text-${severityConfig.color}-400 border border-${severityConfig.color}-500/20`}>
                  {severityConfig.label}
                </span>
                {errorDoc.errorCode && (
                  <span className="px-2 py-0.5 rounded text-xs font-mono bg-slate-700/50 text-slate-300">
                    {errorDoc.errorCode}
                  </span>
                )}
                {errorDoc.isTest && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-500/20 text-amber-400">
                    TEST
                  </span>
                )}
              </div>

              {/* Message */}
              <p className="text-sm text-white break-words">{errorDoc.message}</p>

              {/* Meta Row */}
              <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTimestamp(errorDoc.createdAt)}
                </span>
                <span>•</span>
                <span>{tenantName}</span>
                {errorDoc.location && (
                  <>
                    <span>•</span>
                    <span className="flex items-center gap-1 font-mono text-slate-400">
                      <MapPin className="w-3 h-3" />
                      {errorDoc.location}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {errorDoc.resolved ? (
                canUndo ? (
                  <button
                    onClick={onUndo}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                  >
                    <Undo2 className="w-3 h-3" />
                    Undo
                  </button>
                ) : null
              ) : userIsPlatformAdmin ? (
                <button
                  onClick={onResolve}
                  disabled={isResolving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  {isResolving ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Resolving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-3 h-3" />
                      Resolve
                    </>
                  )}
                </button>
              ) : null}

              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
              >
                <Code className="w-3 h-3" />
                {expanded ? 'Hide' : 'Details'}
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>
          </div>

          {/* Resolved Status */}
          {errorDoc.resolved && (
            <div className="flex items-center gap-3 mt-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <div className="flex-1">
                <p className="text-xs text-emerald-400 font-medium">
                  Resolved {formatTimestamp(errorDoc.resolvedAt)}
                  {resolutionTypeLabel && ` • ${resolutionTypeLabel}`}
                </p>
                {errorDoc.resolutionNote && (
                  <p className="text-xs text-emerald-300/70 mt-0.5">{errorDoc.resolutionNote}</p>
                )}
              </div>
            </div>
          )}

          {/* Expanded Details */}
          {expanded && (
            <div className="mt-4 space-y-4">
              {/* Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
                {/* Who */}
                <div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1.5">
                    <User className="w-3 h-3" />
                    Who
                  </div>
                  <div className="space-y-1 text-xs">
                    <p className="text-slate-300">
                      <span className="text-slate-500">Role:</span> {errorDoc.actor?.role || '—'}
                    </p>
                    <p className="text-slate-300 font-mono">
                      <span className="text-slate-500 font-sans">UID:</span> {errorDoc.actor?.uid?.substring(0, 12) || '—'}...
                    </p>
                  </div>
                </div>

                {/* Where */}
                <div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1.5">
                    <MapPin className="w-3 h-3" />
                    Where
                  </div>
                  <div className="space-y-1 text-xs">
                    <p className="text-slate-300">
                      <span className="text-slate-500">Feature:</span> {errorDoc.where?.feature || errorDoc.location || '—'}
                    </p>
                    <p className="text-slate-300">
                      <span className="text-slate-500">Route:</span> {errorDoc.route || '—'}
                    </p>
                    {errorDoc.where?.screen && (
                      <p className="text-slate-300">
                        <span className="text-slate-500">Screen:</span> {errorDoc.where.screen}
                      </p>
                    )}
                  </div>
                </div>

                {/* Entity Links */}
                <div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1.5">
                    <Link2 className="w-3 h-3" />
                    Entities
                  </div>
                  <div className="space-y-1 text-xs">
                    {errorDoc.entity?.referralId || errorDoc.context?.referralId ? (
                      <p className="text-slate-300 font-mono truncate">
                        <span className="text-slate-500 font-sans">Referral:</span> {errorDoc.entity?.referralId || errorDoc.context?.referralId}
                      </p>
                    ) : null}
                    {errorDoc.entity?.runId || errorDoc.context?.runId ? (
                      <p className="text-slate-300 font-mono truncate">
                        <span className="text-slate-500 font-sans">Run:</span> {errorDoc.entity?.runId || errorDoc.context?.runId}
                      </p>
                    ) : null}
                    {errorDoc.entity?.packId || errorDoc.context?.packId ? (
                      <p className="text-slate-300 font-mono truncate">
                        <span className="text-slate-500 font-sans">Pack:</span> {errorDoc.entity?.packId || errorDoc.context?.packId}
                      </p>
                    ) : null}
                    {!errorDoc.entity?.referralId && !errorDoc.context?.referralId &&
                     !errorDoc.entity?.runId && !errorDoc.context?.runId &&
                     !errorDoc.entity?.packId && !errorDoc.context?.packId && (
                      <p className="text-slate-500">No linked entities</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Firestore Path */}
              {(errorDoc.path || errorDoc.lastFirestorePath) && (
                <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2">
                    <Shield className="w-3 h-3" />
                    Firestore Path
                    {errorDoc.lastOperation && (
                      <span className="ml-2 px-1.5 py-0.5 bg-slate-700 rounded text-slate-400">
                        {errorDoc.lastOperation}
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-mono text-slate-300 break-all">
                    {errorDoc.path || errorDoc.lastFirestorePath}
                  </p>
                </div>
              )}

              {/* Stack Trace */}
              {(errorDoc.stack || errorDoc.stackPreview) && (
                <details className="group">
                  <summary className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer hover:text-slate-300">
                    <Code className="w-3 h-3" />
                    Stack Trace
                    <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
                  </summary>
                  <pre className="mt-2 p-3 bg-slate-900/80 rounded-lg border border-slate-700/50 text-xs font-mono text-slate-300 overflow-x-auto whitespace-pre-wrap">
                    {errorDoc.stack || errorDoc.stackPreview}
                  </pre>
                </details>
              )}

              {/* Context/Details JSON */}
              {(errorDoc.details || errorDoc.context) && Object.keys(errorDoc.details || errorDoc.context).length > 0 && (
                <details className="group">
                  <summary className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer hover:text-slate-300">
                    <Code className="w-3 h-3" />
                    Raw Context
                    <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
                  </summary>
                  <pre className="mt-2 p-3 bg-slate-900/80 rounded-lg border border-slate-700/50 text-xs font-mono text-slate-300 overflow-x-auto">
                    {JSON.stringify(errorDoc.details || errorDoc.context, null, 2)}
                  </pre>
                </details>
              )}

              {/* Copy Debug Bundle */}
              <div className="flex justify-end">
                <button
                  onClick={copyDebugBundle}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
                >
                  <Copy className="w-3 h-3" />
                  {copied ? 'Copied!' : 'Copy debug bundle'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN OPS VIEW COMPONENT
// ============================================================================

const OpsView = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState({});
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use primitive values for filters to prevent infinite re-render loops
  const [filterCategory, setFilterCategory] = useState('');
  const [filterResolved, setFilterResolved] = useState('false');
  const [filterDaysBack, setFilterDaysBack] = useState(7);

  // Resolve modal state
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [errorToResolve, setErrorToResolve] = useState(null);
  const [resolvingErrorId, setResolvingErrorId] = useState(null);

  // Undo state - track recently resolved errors with timestamps
  const [recentlyResolved, setRecentlyResolved] = useState({});

  // Test injection state (platform admin only)
  const [showTestTools, setShowTestTools] = useState(false);
  const [testCategory, setTestCategory] = useState('runtime_error');
  const [testMessage, setTestMessage] = useState('TEST: Forced error from platform admin');
  const [injecting, setInjecting] = useState(false);
  const [injectSuccess, setInjectSuccess] = useState(null);
  const [injectError, setInjectError] = useState(null);

  // Check if user is platform admin
  const userIsPlatformAdmin = user?.role === 'platform_admin' || isPlatformAdmin(user?.role);

  // Load tenants on mount
  useEffect(() => {
    const loadTenants = async () => {
      try {
        const tenantsData = await getTenants();
        setTenants(tenantsData || {});
      } catch (err) {
        console.error('[OpsView] Failed to load tenants:', err);
        setError('Failed to load tenants');
      }
    };

    loadTenants();
  }, []);

  // Memoized load errors function
  const loadErrors = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('[OpsView] Loading errors for tenant:', selectedTenantId || 'all', 'filters:', {
        category: filterCategory,
        resolved: filterResolved,
        daysBack: filterDaysBack,
      });

      const resolved = filterResolved === 'true' ? true : filterResolved === 'false' ? false : null;

      const errorsData = await getOpsErrors(selectedTenantId || null, {
        category: filterCategory || null,
        resolved,
        daysBack: filterDaysBack,
        limit: 200,
      });

      setErrors(errorsData || []);
      console.log('[OpsView] Loaded errors:', errorsData?.length || 0);
    } catch (err) {
      console.error('[OpsView] Failed to load errors:', err);
      setError(`Failed to load errors: ${safeUserError(err)}`);
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId, filterCategory, filterResolved, filterDaysBack]);

  // Load ops errors when tenant or filters change
  useEffect(() => {
    loadErrors();
  }, [loadErrors]);

  // Handle test error injection
  const handleInjectTestError = async () => {
    if (!selectedTenantId) {
      setInjectError('Please select a specific tenant (not "All Tenants")');
      return;
    }

    setInjecting(true);
    setInjectError(null);
    setInjectSuccess(null);

    try {
      console.log('[OpsView] Injecting test error for tenant:', selectedTenantId);

      const errorId = await injectOpsTestError(selectedTenantId, {
        category: testCategory,
        message: testMessage,
        injectedByUid: user?.uid || user?.id || 'platform_admin',
        injectedByRole: user?.role || 'platform_admin',
      });

      setInjectSuccess(`Test error injected successfully (ID: ${errorId})`);
      console.log('[OpsView] ✅ Test error injected:', errorId);

      // Reload errors to show the new one
      await loadErrors();

      // Clear success message after 5 seconds
      setTimeout(() => setInjectSuccess(null), 5000);
    } catch (err) {
      console.error('[OpsView] Failed to inject test error:', err);
      setInjectError(`Failed to inject: ${safeUserError(err)}`);
    } finally {
      setInjecting(false);
    }
  };

  // Open resolve modal
  const handleOpenResolveModal = (errorDoc) => {
    setErrorToResolve(errorDoc);
    setResolveModalOpen(true);
  };

  // Handle resolve confirmation from modal
  const handleResolveConfirm = async (resolutionData) => {
    if (!user?.uid || !errorToResolve) return;

    setResolvingErrorId(errorToResolve.id);

    try {
      console.log('[OpsView] Marking error resolved:', errorToResolve.id, resolutionData);

      await markOpsErrorResolved(
        errorToResolve.tenantId,
        errorToResolve.id,
        user.uid,
        resolutionData
      );

      // Track as recently resolved for undo
      setRecentlyResolved(prev => ({
        ...prev,
        [errorToResolve.id]: Date.now(),
      }));

      // Clear undo option after 10 seconds
      setTimeout(() => {
        setRecentlyResolved(prev => {
          const updated = { ...prev };
          delete updated[errorToResolve.id];
          return updated;
        });
      }, 10000);

      // Close modal
      setResolveModalOpen(false);
      setErrorToResolve(null);

      // Reload errors
      await loadErrors();

      console.log('[OpsView] ✅ Error marked resolved');
    } catch (err) {
      console.error('[OpsView] Failed to mark resolved:', err);
      alert(`Failed to mark resolved: ${safeUserError(err)}`);
    } finally {
      setResolvingErrorId(null);
    }
  };

  // Handle undo resolution
  const handleUndoResolution = async (errorDoc) => {
    try {
      console.log('[OpsView] Undoing resolution for:', errorDoc.id);

      await undoOpsErrorResolution(errorDoc.id);

      // Remove from recently resolved
      setRecentlyResolved(prev => {
        const updated = { ...prev };
        delete updated[errorDoc.id];
        return updated;
      });

      // Reload errors
      await loadErrors();

      console.log('[OpsView] ✅ Resolution undone');
    } catch (err) {
      console.error('[OpsView] Failed to undo resolution:', err);
      alert(`Failed to undo: ${safeUserError(err)}`);
    }
  };

  // Check if an error can be undone (within 10 second window)
  const canUndo = (errorId) => {
    const resolvedAt = recentlyResolved[errorId];
    if (!resolvedAt) return false;
    return Date.now() - resolvedAt < 10000;
  };

  const tenantList = Object.values(tenants);
  const unresolvedCount = errors.filter(e => !e.resolved).length;
  const categoryCounts = errors.reduce((acc, err) => {
    acc[err.category] = (acc[err.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 mb-4">
          <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
          <span className="text-xs font-medium text-red-400 uppercase">Platform Admin</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-light text-white mb-2">Operations Dashboard</h2>
        <p className="text-slate-400">Monitor failures, debug issues, and track platform health</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-400">Error</p>
            <p className="text-sm text-red-300/80 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-400" />
            <div>
              <p className="text-2xl font-semibold text-white">{unresolvedCount}</p>
              <p className="text-xs text-slate-500">Unresolved</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <XCircle className="w-6 h-6 text-red-400" />
            <div>
              <p className="text-2xl font-semibold text-white">{categoryCounts.submit_failed || 0}</p>
              <p className="text-xs text-slate-500">Submit Failures</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-400" />
            <div>
              <p className="text-2xl font-semibold text-white">{categoryCounts.permission_denied || 0}</p>
              <p className="text-xs text-slate-500">Permission Errors</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-purple-400" />
            <div>
              <p className="text-2xl font-semibold text-white">{categoryCounts.runtime_error || 0}</p>
              <p className="text-xs text-slate-500">Runtime Errors</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-emerald-400" />
            <div>
              <p className="text-2xl font-semibold text-white">{errors.filter(e => e.resolved).length}</p>
              <p className="text-xs text-slate-500">Resolved</p>
            </div>
          </div>
        </div>
      </div>

      {/* Test Tools Panel (Platform Admin Only) */}
      {userIsPlatformAdmin && (
        <div className="bg-slate-800/40 border border-amber-500/30 rounded-xl mb-6 overflow-hidden">
          <button
            onClick={() => setShowTestTools(!showTestTools)}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-800/60 transition-colors"
          >
            <div className="flex items-center gap-3">
              <FlaskConical className="w-5 h-5 text-amber-400" />
              <span className="text-sm font-medium text-amber-400">Test Tools</span>
              <span className="text-xs text-slate-500">(Platform Admin)</span>
            </div>
            {showTestTools ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </button>

          {showTestTools && (
            <div className="p-4 border-t border-slate-700/50">
              <p className="text-xs text-slate-400 mb-4">
                Inject a test error to verify the Ops Dashboard is working correctly.
                Each category injects different diagnostic fields.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Category */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">Category</label>
                  <select
                    value={testCategory}
                    onChange={(e) => setTestCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500/50"
                  >
                    <option value="submit_failed">Submit Failed</option>
                    <option value="permission_denied">Permission Denied</option>
                    <option value="rollup_failed">Rollup Failed</option>
                    <option value="runtime_error">Runtime Error</option>
                  </select>
                </div>

                {/* Message */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-slate-400 mb-2">Message</label>
                  <input
                    type="text"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    placeholder="Test error message..."
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500/50"
                  />
                </div>
              </div>

              {/* Inject feedback messages */}
              {injectError && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-xs text-red-400">{injectError}</p>
                </div>
              )}
              {injectSuccess && (
                <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-xs text-emerald-400">{injectSuccess}</p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  {selectedTenantId
                    ? `Will inject into: ${tenants[selectedTenantId]?.name || selectedTenantId}`
                    : '⚠️ Select a specific tenant above (not "All Tenants")'}
                </p>
                <button
                  onClick={handleInjectTestError}
                  disabled={injecting || !selectedTenantId}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {injecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Injecting...
                    </>
                  ) : (
                    <>
                      <FlaskConical className="w-4 h-4" />
                      Inject Test Error
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Filter className="w-5 h-5 text-slate-400" />
          <h3 className="text-sm font-medium text-white">Filters</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Tenant Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-2">Tenant</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50"
            >
              <option value="">All Tenants</option>
              {tenantList.map((tenant) => {
                const tenantId = tenant.tenantId || tenant.id;
                return (
                  <option key={tenantId} value={tenantId}>
                    {tenant.name}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-2">Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50"
            >
              <option value="">All Categories</option>
              <option value="submit_failed">Submit Failed</option>
              <option value="permission_denied">Permission Denied</option>
              <option value="rollup_failed">Rollup Failed</option>
              <option value="runtime_error">Runtime Error</option>
            </select>
          </div>

          {/* Resolved Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-2">Status</label>
            <select
              value={filterResolved}
              onChange={(e) => setFilterResolved(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50"
            >
              <option value="false">Unresolved</option>
              <option value="true">Resolved</option>
              <option value="">All</option>
            </select>
          </div>

          {/* Days Back Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-2">Time Range</label>
            <select
              value={filterDaysBack}
              onChange={(e) => setFilterDaysBack(parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50"
            >
              <option value="1">Last 24 hours</option>
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={loadErrors}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Errors List */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-700/50">
          <h3 className="text-lg font-medium text-white">
            Errors ({errors.length})
          </h3>
        </div>

        {loading && !errors.length ? (
          <div className="p-12 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-400">Loading errors...</p>
          </div>
        ) : errors.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <p className="text-slate-400">No errors found with current filters</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {errors.map((errorDoc) => (
              <ErrorCard
                key={errorDoc.id}
                errorDoc={errorDoc}
                tenantName={tenants[errorDoc.tenantId]?.name || errorDoc.tenantId}
                onResolve={() => handleOpenResolveModal(errorDoc)}
                onUndo={() => handleUndoResolution(errorDoc)}
                isResolving={resolvingErrorId === errorDoc.id}
                canUndo={canUndo(errorDoc.id)}
                userIsPlatformAdmin={userIsPlatformAdmin}
              />
            ))}
          </div>
        )}
      </div>

      {/* Resolve Modal */}
      <ResolveModal
        isOpen={resolveModalOpen}
        onClose={() => {
          setResolveModalOpen(false);
          setErrorToResolve(null);
        }}
        onConfirm={handleResolveConfirm}
        errorDoc={errorToResolve}
        isResolving={resolvingErrorId !== null}
      />
    </div>
  );
};

export default OpsView;
